import { Suspense } from "react";
import { useLocation } from "react-router-dom";
import { lazyWithRetry as lazy } from "@/lib/lazy-retry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/theme-provider";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CartProvider } from "@/contexts/CartContext";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import { BusinessAuthProvider } from "@/contexts/BusinessAuthContext";
import { MimicProvider } from "@/contexts/MimicContext";
import { DemoModeProvider } from "@/contexts/DemoModeContext";
import { IS_DEMO_EMBED } from "@/integrations/supabase/client";
import { DemoReadOnlyBadge } from "@/components/DemoReadOnlyBadge";
import { MimicTransitionOverlay } from "@/components/dashboard/MimicTransitionOverlay";
import { PinLockOverlay } from "@/components/applock/PinLockOverlay";
import { SwUpdateToast } from "@/components/SwUpdateToast";
import { ForceLightMode } from "@/components/ForceLightMode";
import { MarketingWrapper } from "@/components/MarketingWrapper";
import { LegacyRedirect } from "@/marketing/components/LegacyRedirect";
import SubdomainRouter from "@/components/SubdomainRouter";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { VoiceCallProvider } from "@/contexts/VoiceCallContext";
import { AutoDialerProvider } from "@/contexts/AutoDialerContext";
import { CopilotVoiceProvider } from "@/contexts/CopilotVoiceContext";

// ── Lazy-loaded page components (code splitting) ──
const Home = lazy(() => import("./marketing/pages/Home"));
const PublicFormPage = lazy(() => import("./pages/PublicFormPage"));
const PublicQuotePage = lazy(() => import("./pages/PublicQuotePage"));
const PublicSubmissionView = lazy(() => import("./pages/PublicSubmissionView"));
const FeaturesPage = lazy(() => import("./marketing/pages/FeaturesPage"));
const PricingPage = lazy(() => import("./marketing/pages/PricingPage"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const ContactPage = lazy(() => import("./marketing/pages/ContactPage"));
const AiPage = lazy(() => import("./marketing/pages/AiPage"));
const ReferralProgramPage = lazy(() => import("./marketing/pages/ReferralProgramPage"));
const DemoPage = lazy(() => import("./marketing/pages/DemoPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Storefront = lazy(() => import("./pages/Storefront"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const OrderHistory = lazy(() => import("./pages/OrderHistory"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const BusinessDashboard = lazy(() => import("./pages/BusinessDashboard"));
const DemoEmbed = lazy(() => import("./pages/DemoEmbed"));
const ClientBrandingSettings = lazy(() => import("./pages/ClientBrandingSettings"));
const ClientConfigurationSettings = lazy(() => import("./pages/ClientConfigurationSettings"));
const AddClient = lazy(() => import("./pages/AddClient"));
const ManageClient = lazy(() => import("./pages/ManageClient"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const GiftCardClaimPage = lazy(() => import("./pages/GiftCardClaimPage"));
const BusinessCard = lazy(() => import("./pages/BusinessCard"));
const PublicTestPage = lazy(() => import("./pages/PublicTestPage"));
const SharedAnalysis = lazy(() => import("./pages/SharedAnalysis"));
const PublicLeadView = lazy(() => import("./pages/PublicLeadView"));
const GoogleOAuthCallback = lazy(() => import("./pages/GoogleOAuthCallback"));
const MetaOAuthCallback = lazy(() => import("./pages/MetaOAuthCallback"));
const SquareOAuthCallback = lazy(() => import("./pages/SquareOAuthCallback"));
const DialpadOAuthCallback = lazy(() => import("./pages/DialpadOAuthCallback"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const ReferralPortal = lazy(() => import("./pages/portal/ReferralPortal"));
const CustomerPortal = lazy(() => import("./pages/portal/CustomerPortal"));
const SharedFile = lazy(() => import("./pages/SharedFile"));
const SharedFolder = lazy(() => import("./pages/SharedFolder"));
const HowToWatch = lazy(() => import("./pages/HowToWatch"));
// Scripted stand-in dialer for the marketing live embed (P7-W4) — loaded only
// in demo mode, so regular users never pay for the chunk.
const DemoDialerFab = lazy(() => import("./components/demo/DemoDialerFab"));
// The real dialer FAB drags the entire comms subtree (LeadCommsTab, Twilio
// voice SDK, waveform player…) with it — lazy + route-gated so only CRM
// visitors ever download that chunk. Marketing/storefront/auth never pay it.
const GlobalDialerFab = lazy(() =>
  import("./components/GlobalDialerFab").then((m) => ({ default: m.GlobalDialerFab }))
);

// Third floating bubble: internal team messaging. Route-gated + lazy so the
// chunk (realtime chat + media) only loads on CRM surfaces.
const TeamChatFab = lazy(() =>
  import("./components/dashboard/team-chat/TeamChatFab").then((m) => ({ default: m.TeamChatFab }))
);

/** Routes that host the CRM surfaces — matches VISIBLE_PREFIXES inside
 *  GlobalDialerFab (which stays as belt-and-braces). Gating BEFORE the lazy
 *  component keeps the chunk unfetched elsewhere, not merely hidden. */
function DialerFabGate() {
  const { pathname } = useLocation();
  // CRM only — not the developer /dashboard shell
  const visible = pathname.startsWith("/business/dashboard");
  if (!visible) return null;
  return (
    <Suspense fallback={null}>
      <GlobalDialerFab />
    </Suspense>
  );
}

/** Team messaging bubble — CRM only (not developer platform dashboard). */
function TeamChatFabGate() {
  const { pathname } = useLocation();
  const visible = pathname.startsWith("/business/dashboard");
  if (!visible) return null;
  return (
    <Suspense fallback={null}>
      <TeamChatFab />
    </Suspense>
  );
}

// Cached-by-default data layer: revisiting a tab within staleTime paints
// instantly from cache instead of refetching behind a spinner. Freshness
// after writes is handled by explicit invalidateQueries in mutations —
// not by refetch-on-mount — so a short staleTime is safe.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Lightweight loading fallback for lazy route Suspense — matches a common page shell */
function PageLoader() {
  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-40 bg-muted rounded animate-pulse" />
            <div className="h-3 w-56 bg-muted rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="h-32 rounded-xl bg-muted animate-pulse" />
          <div className="h-32 rounded-xl bg-muted animate-pulse" />
          <div className="h-32 rounded-xl bg-muted animate-pulse" />
        </div>
      </div>
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <BusinessAuthProvider>
        <CartProvider>
          <CustomerAuthProvider>
            <MimicProvider>
            <DemoModeProvider>
            <MimicTransitionOverlay />
            {!IS_DEMO_EMBED && <PinLockOverlay />}
            {IS_DEMO_EMBED && <DemoReadOnlyBadge />}
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <SwUpdateToast />
                <ScrollToTop />
                <ErrorBoundary>
                  <VoiceCallProvider>
                  <AutoDialerProvider>
                  {/* ONE live Grok voice call for the whole app — survives a
                      record sheet closing (roaming) + reattaches from the FAB. */}
                  <CopilotVoiceProvider>
                  <SubdomainRouter>
                    {IS_DEMO_EMBED ? (
                      /* Demo embed: fake dialer — looks wired, never dials (P7-W4). */
                      <Suspense fallback={null}>
                        <DemoDialerFab />
                      </Suspense>
                    ) : (
                      <>
                        <DialerFabGate />
                        <TeamChatFabGate />
                      </>
                    )}
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                      <Route path="/" element={<ForceLightMode><MarketingWrapper><Home /></MarketingWrapper></ForceLightMode>} />
                      <Route path="/platform" element={<ForceLightMode><MarketingWrapper><FeaturesPage /></MarketingWrapper></ForceLightMode>} />
                      {/* Renamed P8: /features → /platform, /ai → /meet-grok (hash + query preserved) */}
                      <Route path="/features" element={<LegacyRedirect to="/platform" />} />
                      <Route path="/pricing" element={<ForceLightMode><MarketingWrapper><PricingPage /></MarketingWrapper></ForceLightMode>} />
                      <Route path="/contact" element={<ForceLightMode><MarketingWrapper><ContactPage /></MarketingWrapper></ForceLightMode>} />
                      <Route path="/meet-grok" element={<ForceLightMode><MarketingWrapper><AiPage /></MarketingWrapper></ForceLightMode>} />
                      <Route path="/ai" element={<LegacyRedirect to="/meet-grok" />} />
                      <Route path="/referral-program" element={<ForceLightMode><MarketingWrapper><ReferralProgramPage /></MarketingWrapper></ForceLightMode>} />
                      <Route path="/privacy" element={<ForceLightMode><Privacy /></ForceLightMode>} />
                      <Route path="/terms" element={<ForceLightMode><Terms /></ForceLightMode>} />
                      <Route path="/dashboard" element={<ForceLightMode><ProtectedRoute requireDeveloper><Dashboard /></ProtectedRoute></ForceLightMode>} />
                      <Route path="/dashboard/add-client" element={<ForceLightMode><ProtectedRoute requireDeveloper><AddClient /></ProtectedRoute></ForceLightMode>} />
                      <Route path="/dashboard/client/:businessId" element={<ForceLightMode><ProtectedRoute requireDeveloper><ManageClient /></ProtectedRoute></ForceLightMode>} />

                      <Route path="/dashboard/client/:businessId/branding" element={<ForceLightMode><ProtectedRoute requireDeveloper><ClientBrandingSettings /></ProtectedRoute></ForceLightMode>} />
                      <Route path="/dashboard/client/:businessId/configuration" element={<ForceLightMode><ProtectedRoute requireDeveloper><ClientConfigurationSettings /></ProtectedRoute></ForceLightMode>} />
                      <Route path="/demo" element={<ForceLightMode><MarketingWrapper><DemoPage /></MarketingWrapper></ForceLightMode>} />
                      <Route path="/store/:subdomain" element={<Storefront />} />
                      <Route path="/store/:subdomain/checkout" element={<Checkout />} />
                      <Route path="/store/:subdomain/order/:orderId" element={<OrderConfirmation />} />
                      <Route path="/store/:subdomain/orders" element={<OrderHistory />} />
                      <Route path="/card/:subdomain/:slug" element={<BusinessCard />} />
                      <Route path="/claim/:token" element={<ForceLightMode><GiftCardClaimPage /></ForceLightMode>} />
                      <Route path="/f/:token" element={<ForceLightMode><PublicFormPage /></ForceLightMode>} />
                      <Route path="/q/:token" element={<ForceLightMode><PublicQuotePage /></ForceLightMode>} />
                      <Route path="/s/:token" element={<ForceLightMode><PublicSubmissionView /></ForceLightMode>} />
                      <Route path="/packages" element={<Navigate to="/pricing" replace />} />
                      <Route path="/auth" element={<ForceLightMode><Auth /></ForceLightMode>} />
                      <Route path="/auth/callback" element={<ForceLightMode><AuthCallback /></ForceLightMode>} />
                      <Route path="/reset-password" element={<ForceLightMode><ResetPassword /></ForceLightMode>} />
                      <Route path="/onboarding" element={<ForceLightMode><Onboarding /></ForceLightMode>} />
                      <Route path="/demo-embed" element={<ForceLightMode><DemoEmbed /></ForceLightMode>} />
                      {/* No ForceLightMode — CRM supports light/dark/auto (ui/crm-midnight). Marketing routes keep ForceLightMode. */}
                      <Route path="/business/dashboard/*" element={<ProtectedRoute><BusinessDashboard /></ProtectedRoute>} />
                      <Route path="/pitch-deck" element={<ForceLightMode><ComingSoon /></ForceLightMode>} />
                      <Route path="/test/:agentId" element={<PublicTestPage />} />
                      <Route path="/analysis/:id" element={<ForceLightMode><SharedAnalysis /></ForceLightMode>} />
                      <Route path="/share/lead/:token" element={<ForceLightMode><PublicLeadView /></ForceLightMode>} />
                      {/* Public: the Google redirect URI is the fixed apex host, but the
                          user's session lives on their subdomain/custom domain. The callback
                          authenticates via the signed `state` (not a session) and then sends
                          the browser back to the originating host — so it must NOT be behind
                          ProtectedRoute, or the apex (no session) ejects it to /auth. */}
                      <Route path="/integrations/google/callback" element={<ForceLightMode><GoogleOAuthCallback /></ForceLightMode>} />
                      <Route path="/integrations/meta/callback" element={<ForceLightMode><ProtectedRoute><MetaOAuthCallback /></ProtectedRoute></ForceLightMode>} />
                      <Route path="/integrations/square/callback" element={<ForceLightMode><ProtectedRoute><SquareOAuthCallback /></ProtectedRoute></ForceLightMode>} />
                      <Route path="/integrations/dialpad/callback" element={<ForceLightMode><ProtectedRoute><DialpadOAuthCallback /></ProtectedRoute></ForceLightMode>} />
                      <Route path="/referrals/:slug" element={<ForceLightMode><ReferralPortal /></ForceLightMode>} />
                      <Route path="/portal/:businessId" element={<ForceLightMode><CustomerPortal /></ForceLightMode>} />
                      <Route path="/s/:token" element={<ForceLightMode><SharedFile /></ForceLightMode>} />
                      <Route path="/sf/:token" element={<ForceLightMode><SharedFolder /></ForceLightMode>} />
                      <Route path="/how-to/watch/:token" element={<ForceLightMode><HowToWatch /></ForceLightMode>} />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<ForceLightMode><NotFound /></ForceLightMode>} />
                      </Routes>
                    </Suspense>
                  </SubdomainRouter>
                  </CopilotVoiceProvider>
                  </AutoDialerProvider>
                  </VoiceCallProvider>
                </ErrorBoundary>
              </BrowserRouter>
            </TooltipProvider>
            </DemoModeProvider>
            </MimicProvider>
          </CustomerAuthProvider>
        </CartProvider>
      </BusinessAuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;